/**
 * Phase F11 FE-3 — proctor event timeline.
 *
 * Renders the chronological list of :class:`ReviewProctorEvent` rows
 * for one attempt. Sits inside :class:`ProctorReviewPanel` next to
 * the artifact gallery.
 *
 * Each event becomes a single row with:
 *   * a kind-specific icon + colour-coded pill (severity inferred from
 *     the kind — ``consent_denied`` / ``devtools_detected`` are
 *     ``destructive``; ``paste_blocked`` / ``copy_blocked`` /
 *     ``mouse_left_window`` / ``tab_blurred`` are ``warning``;
 *     everything else is ``muted``)
 *   * a relative time stamp anchored to ``occurred_at``, plus a
 *     full ISO tooltip on hover for forensic traceability
 *   * the ``metadata`` payload pretty-printed inline when present
 *
 * Iron Rule #16 (data display): we don't try to re-implement
 * ``DataTable`` here because the timeline is read-only, single-column,
 * and benefits from a verbose card-stack layout over a tabular one.
 *
 * Phase F12 FE-1 surfaces the deterministic cheat-detection
 * verdict in a sibling :class:`CheatAssessmentSummary` component
 * (mounted above this timeline by ``ProctorAttemptDetail``). The
 * verdict is the answer to "should I escalate?"; this timeline
 * remains the per-event audit trail and intentionally does not
 * duplicate the score breakdown.
 */
import {
  AlertOctagon,
  CheckCircle2,
  Clipboard,
  Eye,
  EyeOff,
  Maximize2,
  Minimize2,
  MousePointerClick,
  ShieldAlert,
  ShieldOff,
  Wifi,
  WifiOff,
} from "lucide-react";
import { Badge } from "@eduviaos/ui-kit";
import type { ReviewProctorEvent, ReviewProctorEventKind } from "@eduviaos/shared";

interface ProctorEventTimelineProps {
  events: ReviewProctorEvent[];
}

type EventSeverity = "destructive" | "warning" | "muted";

const _SEVERITY_BY_KIND: Record<ReviewProctorEventKind, EventSeverity> = {
  consent_granted: "muted",
  consent_denied: "destructive",
  fullscreen_entered: "muted",
  fullscreen_exited: "warning",
  tab_blurred: "warning",
  tab_focused: "muted",
  mouse_left_window: "warning",
  mouse_returned: "muted",
  paste_blocked: "warning",
  copy_blocked: "warning",
  right_click_blocked: "warning",
  devtools_detected: "destructive",
  audio_test_passed: "muted",
  audio_test_failed: "destructive",
  speed_test_result: "muted",
  network_degraded: "warning",
  network_restored: "muted",
};

const _LABEL_BY_KIND: Record<ReviewProctorEventKind, string> = {
  consent_granted: "Consent granted",
  consent_denied: "Consent revoked",
  fullscreen_entered: "Entered fullscreen",
  fullscreen_exited: "Exited fullscreen",
  tab_blurred: "Tab blurred",
  tab_focused: "Tab focused",
  mouse_left_window: "Mouse left window",
  mouse_returned: "Mouse returned",
  paste_blocked: "Paste blocked",
  copy_blocked: "Copy blocked",
  right_click_blocked: "Right-click blocked",
  devtools_detected: "DevTools detected",
  audio_test_passed: "Audio test passed",
  audio_test_failed: "Audio test failed",
  speed_test_result: "Speed test result",
  network_degraded: "Network degraded",
  network_restored: "Network restored",
};

export function ProctorEventTimeline({ events }: ProctorEventTimelineProps) {
  if (events.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        No proctor events were recorded for this attempt.
      </p>
    );
  }
  return (
    <ol role="list" aria-label="Proctor event timeline" className="divide-border/60 divide-y">
      {events.map((ev) => (
        <EventRow key={ev.id} event={ev} />
      ))}
    </ol>
  );
}

function EventRow({ event }: { event: ReviewProctorEvent }) {
  const severity = _SEVERITY_BY_KIND[event.kind] ?? "muted";
  const label = _LABEL_BY_KIND[event.kind] ?? event.kind;
  const occurred = new Date(event.occurred_at);
  return (
    <li className="flex items-start gap-3 py-3" data-severity={severity}>
      <div className="mt-0.5 flex-shrink-0">
        <EventIcon kind={event.kind} severity={severity} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-medium leading-tight">{label}</span>
          <Badge
            variant={badgeVariantFor(severity)}
            className="text-[10px] uppercase tracking-wide"
          >
            {severity === "muted" ? "info" : severity}
          </Badge>
          <time
            dateTime={event.occurred_at}
            title={occurred.toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "long",
            })}
            className="text-muted-foreground ml-auto text-xs tabular-nums"
          >
            {formatRelative(occurred)}
          </time>
        </div>
        {hasRenderableMetadata(event.metadata) && (
          <pre
            className="bg-muted/40 text-muted-foreground mt-1 max-w-full overflow-x-auto rounded p-2 text-xs leading-snug"
            aria-label="Event metadata"
          >
            {JSON.stringify(event.metadata, null, 2)}
          </pre>
        )}
      </div>
    </li>
  );
}

function EventIcon({ kind, severity }: { kind: ReviewProctorEventKind; severity: EventSeverity }) {
  const className =
    severity === "destructive"
      ? "h-4 w-4 text-destructive"
      : severity === "warning"
        ? "h-4 w-4 text-amber-500"
        : "text-muted-foreground h-4 w-4";
  switch (kind) {
    case "consent_granted":
      return <CheckCircle2 className={className} aria-hidden />;
    case "consent_denied":
      return <ShieldOff className={className} aria-hidden />;
    case "fullscreen_entered":
      return <Maximize2 className={className} aria-hidden />;
    case "fullscreen_exited":
      return <Minimize2 className={className} aria-hidden />;
    case "tab_blurred":
      return <EyeOff className={className} aria-hidden />;
    case "tab_focused":
      return <Eye className={className} aria-hidden />;
    case "mouse_left_window":
    case "mouse_returned":
      return <MousePointerClick className={className} aria-hidden />;
    case "paste_blocked":
    case "copy_blocked":
      return <Clipboard className={className} aria-hidden />;
    case "right_click_blocked":
      return <ShieldAlert className={className} aria-hidden />;
    case "devtools_detected":
      return <AlertOctagon className={className} aria-hidden />;
    case "audio_test_passed":
    case "audio_test_failed":
      return <ShieldAlert className={className} aria-hidden />;
    case "speed_test_result":
    case "network_restored":
      return <Wifi className={className} aria-hidden />;
    case "network_degraded":
      return <WifiOff className={className} aria-hidden />;
    default:
      return <ShieldAlert className={className} aria-hidden />;
  }
}

function badgeVariantFor(severity: EventSeverity): "danger" | "warn" | "secondary" {
  switch (severity) {
    case "destructive":
      return "danger";
    case "warning":
      return "warn";
    case "muted":
      return "secondary";
  }
}

function hasRenderableMetadata(meta: Record<string, unknown> | null): boolean {
  if (!meta) return false;
  return Object.keys(meta).length > 0;
}

function formatRelative(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  if (Math.abs(diffMs) < 60_000) return "just now";
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
